import type { ThemeOverrideConfig } from '@components/integrated/theme-override';

/**
 * Default dark theme — the night side of the design system.
 *
 * The light palette has a warm, refined character (steel blues + warm stone grays).
 * This dark palette preserves that personality: surfaces carry a subtle warm
 * undertone (not cold zinc), primary blues become luminous steel, and status
 * colors are brightened for dark-background legibility.
 *
 * Surface layering in dark mode:
 *   50  = deepest background (page, sidebar, recessed inputs)
 *   100 = card / panel surface (the main "canvas" in dark mode)
 *   200 = borders and dividers (warm-neutral, clearly visible on 100)
 *
 * Text hierarchy in dark mode:
 *   500 = muted / disabled
 *   600 = secondary text, secondary icons
 *   700 = labels, ghost-button text, primary headings
 *   800 = default body text
 *   900 = strong emphasis
 */
export const defaultDarkTheme: ThemeOverrideConfig = {
  id: 'default-dark',
  colors: {
    primary: {
      50: '#0d1520',
      100: '#152540',
      200: '#1a3558',
      300: '#2a5080',
      400: '#3d75b3',
      500: '#6ba3d0',
      600: '#8cbde0',
      700: '#5b9bd5',
      800: '#a8cde8',
      900: '#caddee',
      950: '#e8f1f8',
    },
    secondary: {
      50: '#111110',
      100: '#1c1b19',
      200: '#2b2926',
      300: '#3d3a37',
      400: '#575451',
      500: '#76726f',
      600: '#9f9b97',
      700: '#c4bfba',
      800: '#dfdbd7',
      900: '#f0ede9',
      950: '#faf8f5',
    },
    accent: {
      50: '#1f0d2b',
      100: '#2d1541',
      200: '#461e6e',
      300: '#6d29a8',
      400: '#9b4ddb',
      500: '#b97af0',
      600: '#d0a0f7',
      700: '#e0c0fb',
      800: '#edd8fc',
      900: '#f6edfd',
      950: '#fbf6fe',
    },
    success: {
      50: '#071f12',
      200: '#0a3d23',
      500: '#3dd68f',
      700: '#7eedc0',
      800: '#b0f5da',
      900: '#e2fbf1',
    },
    warning: {
      50: '#1c1004',
      200: '#3d2508',
      500: '#f7c948',
      700: '#fad97e',
      800: '#fce8a8',
    },
    error: {
      50: '#200808',
      200: '#5c1515',
      500: '#f06060',
      700: '#f7a0a0',
      800: '#fac8c8',
      900: '#fdf0f0',
    },
    info: {
      50: '#081825',
      200: '#0f3052',
      500: '#45b8f0',
      700: '#88d4f8',
      800: '#bce5fb',
    },
  },
  shadows: {
    sm: '0 1px 3px 0 rgb(0 0 0 / 0.5)',
    DEFAULT: '0 2px 4px 0 rgb(0 0 0 / 0.5), 0 1px 2px -1px rgb(0 0 0 / 0.4)',
    md: '0 4px 8px -1px rgb(0 0 0 / 0.55), 0 2px 4px -2px rgb(0 0 0 / 0.4)',
    lg: '0 12px 20px -4px rgb(0 0 0 / 0.55), 0 4px 8px -4px rgb(0 0 0 / 0.4)',
    xl: '0 24px 32px -6px rgb(0 0 0 / 0.55), 0 10px 12px -6px rgb(0 0 0 / 0.4)',
  },
};
